import type {
  Coding,
  Observation,
  ObservationComponent,
  Quantity,
} from '@medplum/fhirtypes'
import type {
  CodingAnswerOption,
  ObservationComponentSpec,
  ObservationTemplate,
} from '#src/codings.ts'

const UCUM = 'http://unitsofmeasure.org'
// How a UCUM code reads on the chart, where it differs from the code.
interface UnitLabels {
  readonly [unit: string]: string
}

const UNIT_LABELS: UnitLabels = {
  'mm[Hg]': 'mmHg',
  '[degF]': '°F',
  Cel: '°C',
  '[lb_av]': 'lb',
  '[in_i]': 'in',
  '{beats}/min': 'bpm',
  '/min': '/min',
}
const PLACEHOLDER = /\{([^}]+)\}/g
const NUMBER = '(-?\\d+(?:\\.\\d+)?)'

export type ComponentValue = number | CodingAnswerOption

// How a part's UCUM unit reads on the chart.
export function unitLabel(unit: string): string {
  return UNIT_LABELS[unit] ?? unit
}

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// The component codes the format names, in the order it names them.
export function formatCodes(template: ObservationTemplate): string[] {
  return [...(template.format ?? '').matchAll(PLACEHOLDER)].map(
    (match) => match[1]
  )
}

// Matches the typed line a template's format describes, one number per
// placeholder, spaces allowed around the separators. Undefined without a
// format.
export function componentPattern(
  template: ObservationTemplate
): RegExp | undefined {
  const { format } = template
  if (!format) {
    return undefined
  }
  const pieces = format.split(PLACEHOLDER)
  // `split` with a capturing group alternates literal text and codes.
  const source = pieces
    .map((piece, index) =>
      index % 2 === 1 ? NUMBER : `\\s*${escapeRegex(piece.trim())}\\s*`
    )
    .join('')
    .replace(/^(\\s\*)+|(\\s\*)+$/g, '')
  return new RegExp(`\\b${source}\\b`)
}

// The required values typed in the line, by component code. Undefined when
// the line does not hold every one of them.
export function parseComponentText(
  template: ObservationTemplate,
  text: string
): Record<string, number> | undefined {
  const pattern = componentPattern(template)
  const match = pattern ? text.match(pattern) : null
  if (!match) {
    return undefined
  }
  return Object.fromEntries(
    formatCodes(template).map((code, index) => [code, Number(match[index + 1])])
  )
}

function componentEntry(
  spec: ObservationComponentSpec,
  value: ComponentValue
): ObservationComponent {
  const code = { coding: [spec.coding], text: spec.coding.display }
  if (typeof value === 'number') {
    const valueQuantity: Quantity = { value }
    if (spec.unit) {
      valueQuantity.unit = unitLabel(spec.unit)
      valueQuantity.system = UCUM
      valueQuantity.code = spec.unit
    }
    return { code, valueQuantity }
  }
  const { code: answerCode, display, system } = value
  const answerCoding: Coding = { code: answerCode, display }
  if (system) {
    answerCoding.system = system
  }
  return {
    code,
    valueCodeableConcept: {
      coding: [answerCoding],
      text: display,
    },
  }
}

function isComponent(component: ObservationComponent, coding: Coding): boolean {
  return (
    component.code?.coding?.some(
      (candidate) =>
        candidate.code === coding.code && candidate.system === coding.system
    ) ?? false
  )
}

function specFor(
  template: ObservationTemplate,
  code: string
): ObservationComponentSpec | undefined {
  return template.components.find((spec) => spec.coding.code === code)
}

// The components for these values, in the template's order. Values for codes
// the template does not name are left out.
export function toComponents(
  template: ObservationTemplate,
  values: Record<string, ComponentValue>
): ObservationComponent[] {
  return template.components.flatMap((spec) => {
    const value = values[spec.coding.code ?? '']
    return value === undefined ? [] : [componentEntry(spec, value)]
  })
}

// The components with one part set, or removed when `value` is undefined.
// The others keep their values; the parts stay in the template's order.
export function withComponentValue(
  template: ObservationTemplate,
  components: ObservationComponent[] | undefined,
  spec: ObservationComponentSpec,
  value: ComponentValue | undefined
): ObservationComponent[] {
  const others = (components ?? []).filter(
    (component) => !isComponent(component, spec.coding)
  )
  const next =
    value === undefined ? others : [...others, componentEntry(spec, value)]
  const order = (component: ObservationComponent): number => {
    const index = template.components.findIndex((candidate) =>
      isComponent(component, candidate.coding)
    )
    return index === -1 ? template.components.length : index
  }
  return [...next].sort((a, b) => order(a) - order(b))
}

// One part's recorded value, matched by code rather than position. A number
// may have been imported as a string.
export function componentValue(
  observation: Pick<Observation, 'component'>,
  coding: Coding
): number | string | CodingAnswerOption | undefined {
  const component = observation.component?.find((candidate) =>
    isComponent(candidate, coding)
  )
  if (!component) {
    return undefined
  }
  if (component.valueQuantity?.value !== undefined) {
    return component.valueQuantity.value
  }
  if (component.valueString) {
    return component.valueString
  }
  const answer = component.valueCodeableConcept?.coding?.[0]
  if (answer?.code) {
    const option: CodingAnswerOption = {
      code: answer.code,
      display:
        answer.display ?? component.valueCodeableConcept?.text ?? answer.code,
    }
    if (answer.system) {
      option.system = answer.system
    }
    return option
  }
  return undefined
}

function valueText(
  spec: ObservationComponentSpec,
  value: number | string | CodingAnswerOption
): string {
  if (typeof value === 'object') {
    return value.display
  }
  return spec.unit ? `${value} ${unitLabel(spec.unit)}` : String(value)
}

// The parts the format names, filled in from the record as they would be
// typed: "120/80". Undefined without a format or when one of them is missing.
export function componentLine(
  template: ObservationTemplate,
  observation: Pick<Observation, 'component'>
): string | undefined {
  const { format } = template
  if (!format) {
    return undefined
  }
  const values = new Map<string, number | string>()
  for (const code of formatCodes(template)) {
    const spec = specFor(template, code)
    const value = spec ? componentValue(observation, spec.coding) : undefined
    if (value === undefined || typeof value === 'object') {
      return undefined
    }
    values.set(code, value)
  }
  return format.replace(PLACEHOLDER, (_, code: string) =>
    String(values.get(code))
  )
}

// The recorded components as the format reads, then any other part that has
// a value: "120/80 mmHg · Position: Sitting". Undefined when a part the format
// names is missing, so the caller can fall back.
export function formatComponents(
  template: ObservationTemplate,
  observation: Pick<Observation, 'component'>
): string | undefined {
  const named = formatCodes(template)
  let line = ''
  if (template.format) {
    const filled = componentLine(template, observation)
    if (filled === undefined) {
      return undefined
    }
    const units = new Set(
      named.map((code) => specFor(template, code)?.unit ?? null)
    )
    const [unit] = [...units]
    line = units.size === 1 && unit ? `${filled} ${unitLabel(unit)}` : filled
  }
  const rest = template.components
    .filter((spec) => !named.includes(spec.coding.code ?? ''))
    .flatMap((spec) => {
      const value = componentValue(observation, spec.coding)
      return value === undefined
        ? []
        : [`${spec.label}: ${valueText(spec, value)}`]
    })
  const parts = [line, ...rest].filter(Boolean)
  return parts.length ? parts.join(' · ') : undefined
}
